import { Bot, Check, Link as LinkIcon, Ship, Lock, Sparkles, ChevronDown, ChevronUp, UserRound } from 'lucide-react'
import { Link, usePage } from '@inertiajs/react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useOrgPath } from '@/lib/pathFor'
import { shortContainerType, BOOKING_STATUS_COLORS } from '@/lib/constants'
import { capitalize } from '@/lib/utils'
import { useState } from 'react'
import type { ThreadDetail, SenderInfo } from '@/pages/Inbox/Index'

interface PageProps {
  current_user?: {
    role: string
    super_admin: boolean
  }
}

interface ContextSidebarProps {
  shipments: ThreadDetail['shipments']
  suggestedShipments: ThreadDetail['suggested_shipments']
  senders?: SenderInfo[]
  agentSession: ThreadDetail['latest_agent_session']
  reviewReasons: string[]
  onOpenBooking?: (bookingId: string) => void
  onAssignShipment?: () => void
  onAssignSuggestedShipment?: (shipmentId: string) => void
  onSuppressShipment?: (threadShipmentId: string) => void
  onPromoteSender?: (sender: SenderInfo) => void
}

function formatShortDate(dateStr: string | null) {
  if (!dateStr) return null
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Section Header component
function SectionHeader({ icon: Icon, title, extra }: { icon: React.ElementType; title: string; extra?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 w-full">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="text-[13px] font-semibold text-foreground font-space-grotesk">
        {title}
      </span>
      {extra && (
        <>
          <div className="flex-1" />
          {extra}
        </>
      )}
    </div>
  )
}

// Single shipment card (collapsible)
function ShipmentCard({
  shipment,
  onOpenBooking,
  onSuppress,
  isFirst
}: {
  shipment: ThreadDetail['shipments'][0]
  onOpenBooking?: (bookingId: string) => void
  onSuppress?: () => void
  isFirst?: boolean
}) {
  const [expanded, setExpanded] = useState(isFirst)
  const pathFor = useOrgPath()

  return (
    <div className="rounded-md border border-border bg-card overflow-hidden">
      {/* Header - always visible */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Ship className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <Link
              href={pathFor(`/shipments/${shipment.id}`)}
              onClick={(e) => e.stopPropagation()}
              className="block text-[13px] font-semibold text-foreground truncate hover:underline"
            >
              {shipment.internal_reference}
            </Link>
            <span className="block text-[11px] text-muted-foreground truncate">
              {shipment.origin_port || '--'} → {shipment.destination_port || '--'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Badge variant="secondary" size="xs">
            {shipment.bookings.length} booking{shipment.bookings.length !== 1 ? 's' : ''}
          </Badge>
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-border px-3 pb-3 pt-2 space-y-3">
          {/* Shipment details */}
          <div className="space-y-1.5">
            {shipment.client_name && (
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">Client</span>
                <span className="text-[11px] font-medium text-foreground">{shipment.client_name}</span>
              </div>
            )}
            {shipment.earliest_sailing && (
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">Earliest Sailing</span>
                <span className="text-[11px] font-medium text-foreground">{formatShortDate(shipment.earliest_sailing)}</span>
              </div>
            )}
          </div>

          {/* Bookings list */}
          {shipment.bookings.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Bookings</div>
              <div className="space-y-1">
                {shipment.bookings.map((booking) => (
                  <button
                    key={booking.id}
                    onClick={() => onOpenBooking?.(booking.id)}
                    className="w-full rounded border border-border bg-background p-2 text-left hover:bg-muted/30 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-foreground">
                        {booking.internal_reference}
                      </span>
                      {booking.status && (
                        <Badge variant="secondary" size="xs" className={BOOKING_STATUS_COLORS[booking.status]}>
                          {capitalize(booking.status)}
                        </Badge>
                      )}
                    </div>
                    {booking.carrier && (
                      <div className="mt-1 flex items-start justify-between gap-2">
                        <span className="text-[11px] text-muted-foreground">Carrier</span>
                        <span className="text-right text-[11px] font-medium text-foreground break-words">
                          {booking.carrier}
                        </span>
                      </div>
                    )}
                    {booking.carrier_booking_id && (
                      <div className="mt-1 flex items-start justify-between gap-2">
                        <span className="text-[11px] text-muted-foreground">Carrier ID</span>
                        <span className="text-right text-[11px] font-medium text-foreground break-all">
                          {booking.carrier_booking_id}
                        </span>
                      </div>
                    )}
                    {booking.container_count && (
                      <div className="mt-1 flex items-start justify-between gap-2">
                        <span className="text-[11px] text-muted-foreground">Containers</span>
                        <span className="text-right text-[11px] font-medium text-foreground">
                          {booking.container_count}x {shortContainerType(booking.container_type) || 'container'}
                        </span>
                      </div>
                    )}
                    {booking.sailing_date && (
                      <div className="mt-1 flex items-start justify-between gap-2">
                        <span className="text-[11px] text-muted-foreground">ETD</span>
                        <span className="text-right text-[11px] font-medium text-foreground">
                          {formatShortDate(booking.sailing_date)}
                        </span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 text-[11px] text-blue-600 hover:text-blue-700 hover:bg-blue-50 cursor-pointer"
              asChild
            >
              <Link href={pathFor(`/shipments/${shipment.id}`)}>
                View Shipment
              </Link>
            </Button>
            {onSuppress && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1 h-7 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer"
                onClick={onSuppress}
              >
                Unlink Shipment
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Unassigned state
function UnassignedSection({
  onAssignShipment,
  suggestedShipments,
  onAssignSuggestedShipment
}: {
  onAssignShipment?: () => void
  suggestedShipments: ThreadDetail['suggested_shipments']
  onAssignSuggestedShipment?: (shipmentId: string) => void
}) {
  const suggested = suggestedShipments[0]

  return (
    <section className="flex flex-col gap-3">
      <div className="rounded-md border border-border bg-card p-3 flex flex-col gap-2.5">
        <p className="text-[11px] font-medium text-muted-foreground">No shipment linked yet</p>

        {suggested && !suggested.suppressed ? (
          <>
            <div className="flex flex-col gap-1">
              <p className="font-space-grotesk text-base font-bold text-foreground">
                {suggested.internal_reference}
              </p>
              <p className="text-[11px] text-muted-foreground">{suggested.origin_port || '--'} → {suggested.destination_port || '--'}</p>
              {suggested.client_name && (
                <p className="text-[11px] text-muted-foreground">{suggested.client_name}</p>
              )}
            </div>

            {suggested.matched_internal_shipment_refs.length > 0 && (
              <div className="flex items-start gap-1.5">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600" />
                <p className="text-[11px] font-medium leading-[1.4] text-blue-700">
                  Likely match via Internal Ref {suggested.matched_internal_shipment_refs.join(', ')}
                </p>
              </div>
            )}

            {suggested.matched_internal_booking_refs.length > 0 && (
              <div className="flex items-start gap-1.5">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600" />
                <p className="text-[11px] font-medium leading-[1.4] text-blue-700">
                  Likely match via Internal Booking Ref {suggested.matched_internal_booking_refs.join(', ')}
                </p>
              </div>
            )}

            {suggested.matched_carrier_booking_ids.length > 0 && (
              <div className="flex items-start gap-1.5">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600" />
                <p className="text-[11px] font-medium leading-[1.4] text-blue-700">
                  Likely match via carrier booking ID {suggested.matched_carrier_booking_ids.join(', ')}
                </p>
              </div>
            )}

            <Button
              onClick={() => onAssignSuggestedShipment?.(suggested.id)}
              className="w-full h-8 text-[12px]"
            >
              <LinkIcon className="mr-1.5 h-3.5 w-3.5" />
              Link to This Shipment
            </Button>

            <Button
              variant="outline"
              onClick={onAssignShipment}
              className="w-full h-8 text-[11px]"
            >
              Link Different Shipment
            </Button>
          </>
        ) : (
          <Button
            onClick={onAssignShipment}
            className="w-full h-9 text-[13px]"
          >
            <LinkIcon className="h-3.5 w-3.5 mr-2" />
            Link to Shipment
          </Button>
        )}
      </div>

      {/* Previously removed suggestions */}
      {suggestedShipments.some(s => s.suppressed) && (
        <div className="space-y-2">
          <p className="text-[11px] text-muted-foreground">Previously removed:</p>
          {suggestedShipments.filter(s => s.suppressed).map(s => (
            <div key={s.id} className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{s.internal_reference} · {s.origin_port || '--'} → {s.destination_port || '--'}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-1.5 text-[10px]"
                onClick={() => onAssignSuggestedShipment?.(s.id)}
              >
                Re-add
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function senderStatusLabel(sender: SenderInfo) {
  if (sender.status === 'known_client') return sender.client_name || 'Known client'
  if (sender.status === 'carrier') return 'Carrier'
  if (sender.status === 'internal') return 'Internal'
  if (sender.status === 'unmatched') return 'Unmatched sender'
  return 'Unknown sender'
}

function senderStatusClass(sender: SenderInfo) {
  if (sender.status === 'known_client') return 'bg-green-100 text-green-800'
  if (sender.status === 'carrier') return 'bg-blue-50 text-blue-700'
  if (sender.status === 'internal') return 'bg-gray-100 text-gray-600'
  return 'bg-amber-100 text-amber-800'
}

function SenderSection({ senders, onPromoteSender }: { senders: SenderInfo[]; onPromoteSender?: (sender: SenderInfo) => void }) {
  const pathFor = useOrgPath()

  if (senders.length === 0) return null

  return (
    <section className="flex flex-col gap-3">
      <SectionHeader icon={UserRound} title="Sender" />

      <div className="space-y-2">
        {senders.slice(0, 3).map((sender) => (
          <div key={sender.email || sender.name || sender.status} className="rounded-md border border-border bg-card p-2.5">
            <div className="space-y-1">
              <Badge variant="secondary" size="xs" className={`max-w-full justify-start truncate ${senderStatusClass(sender)}`}>
                <span className="truncate">{senderStatusLabel(sender)}</span>
              </Badge>
              <p className="truncate text-[13px] font-medium leading-4 text-foreground">{sender.name || sender.email || 'Unknown sender'}</p>
              {sender.email && <p className="truncate text-[11px] leading-4 text-muted-foreground">{sender.email}</p>}
            </div>

            {sender.status === 'known_client' && sender.client_id && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2 h-7 w-full text-[11px] cursor-pointer"
                asChild
              >
                <Link href={pathFor(`/clients/${sender.client_id}`)}>
                  View Client
                </Link>
              </Button>
            )}

            {['unknown_sender', 'unmatched'].includes(sender.status) && sender.email && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2 h-7 w-full text-[11px] cursor-pointer"
                onClick={() => onPromoteSender?.(sender)}
              >
                <UserRound className="mr-1.5 h-3 w-3" />
                Convert to Client
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

// Agent session section (admin only)
function AgentSessionSection({ session }: { session: ThreadDetail['latest_agent_session'] }) {
  const pathFor = useOrgPath()
  const { current_user } = usePage().props as PageProps

  const isAdmin = current_user?.role === 'admin' || current_user?.super_admin
  if (!isAdmin || !session) return null

  function sessionDuration(): string {
    if (!session.started_at || !session.completed_at) return '--'
    const ms = new Date(session.completed_at).getTime() - new Date(session.started_at).getTime()
    const secs = Math.round(ms / 1000)
    return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`
  }

  return (
    <section className="flex flex-col gap-3">
      <SectionHeader
        icon={Bot}
        title="Agent Session"
        extra={<Lock className="h-3 w-3 text-muted-foreground" />}
      />

      <div className="rounded-md border border-border bg-card p-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">Status</span>
          <Badge variant="secondary" size="xs">
            {capitalize(session.status)}
          </Badge>
        </div>

        {session.escalation_reason && (
          <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1">
            {session.escalation_reason}
          </div>
        )}

        {session.tool_calls && session.tool_calls.length > 0 && (
          <div className="space-y-1 pt-1">
            {session.tool_calls.slice(0, 5).map((tc, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Check className="h-3 w-3 text-green-500" />
                <span className="text-xs text-foreground truncate">{tc.tool_name}</span>
              </div>
            ))}
            {session.tool_calls.length > 5 && (
              <p className="text-[10px] text-muted-foreground pl-4.5">
                +{session.tool_calls.length - 5} more
              </p>
            )}
          </div>
        )}

        {session.started_at && session.completed_at && (
          <p className="text-[11px] text-muted-foreground pt-1">
            Completed in {sessionDuration()} · {session.tool_calls?.length || 0} tool calls
          </p>
        )}

        <Link
          href={pathFor(`/agent_sessions/${session.id}`)}
          className="text-[11px] font-medium text-blue-600 hover:underline cursor-pointer"
        >
          View full session
        </Link>
      </div>
    </section>
  )
}

export default function ContextSidebar({
  shipments,
  suggestedShipments,
  senders = [],
  agentSession,
  reviewReasons: _reviewReasons,
  onOpenBooking,
  onAssignShipment,
  onAssignSuggestedShipment,
  onSuppressShipment,
  onPromoteSender,
}: ContextSidebarProps) {

  return (
    <div className="flex h-full w-[380px] flex-col border-l border-border bg-muted/50 overflow-y-auto">
      <div className="flex flex-col gap-5 py-5 px-4">
        {/* Sender */}
        <SenderSection senders={senders} onPromoteSender={onPromoteSender} />

        {/* Shipments Section */}
        <section className="flex flex-col gap-3">
          <SectionHeader
            icon={Ship}
            title={`Shipments (${shipments.length})`}
            extra={shipments.length > 0 && (
              <button
                onClick={onAssignShipment}
                className="text-[11px] font-medium text-blue-600 hover:underline cursor-pointer"
              >
                + Link
              </button>
            )}
          />

          {shipments.length === 0 ? (
            <UnassignedSection
              onAssignShipment={onAssignShipment}
              suggestedShipments={suggestedShipments}
              onAssignSuggestedShipment={onAssignSuggestedShipment}
            />
          ) : (
            <div className="flex flex-col gap-2">
              {shipments.map((shipment, index) => (
                <ShipmentCard
                  key={shipment.id}
                  shipment={shipment}
                  onOpenBooking={onOpenBooking}
                  onSuppress={() => onSuppressShipment?.(shipment.thread_shipment_id)}
                  isFirst={index === 0}
                />
              ))}
            </div>
          )}
        </section>

        {/* Agent Session Section */}
        <AgentSessionSection session={agentSession} />
      </div>
    </div>
  )
}
